import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

import ProfileFormGrid from '../../base/ProfileFormGrid.styled';
import ProfileTextInput from '../../base/inputs/ProfileTextInput';
import ProfileSelectInput from '../../base/inputs/ProfileSelectInput';
import GridColumnPlaceholder from '../../base/GridColumnPlaceholder.styled';
import ProfilePhoneNumberInput from '../../base/inputs/ProfilePhoneNumberInput';

import countries from '../../../../../../public/data/countries.json';
import relationshipOptions from '../../../utils/options/relationshipOptions';

export default function SecondaryEmergencyContactForm({ form, setForm }) {
  const { info, readonly } = useSelector(state => state.travelerProfile);

  const omittedCountryValues = ['Countries Vary', 'Stateless/Refuge/Other', 'Unspecified Location'];
  const countryOptions = countries.filter(country => !omittedCountryValues.includes(country.label));
  const [countryOptionList, setCountryOptionList] = useState([...countryOptions]);
  const [relationshipOptionList, setRelationshipOptionList] = useState([...relationshipOptions]);

  useEffect(() => {
    let newCountryOptionList = [...countryOptionList];
    //country mapping
    if (info.secondary_emergency_contact_address_country) {
      let country = countryOptionList.find(
        option =>
          option.value === info.secondary_emergency_contact_address_country ||
          option.label === info.secondary_emergency_contact_address_country,
      );
      if (!country) {
        newCountryOptionList = [
          ...newCountryOptionList,
          {
            id: Date.now(),
            label: info.secondary_emergency_contact_address_country,
            value: info.secondary_emergency_contact_address_country,
          },
        ];

        setCountryOptionList([...newCountryOptionList]);
      }
    }

    //relationship mapping
    let newRelationshipOptionList = [...relationshipOptionList];
    if (info.secondary_emergency_contact_relationship) {
      let emergencyContactRelationship = relationshipOptionList.find(
        option =>
          option.value === info.secondary_emergency_contact_relationship ||
          option.label === info.secondary_emergency_contact_relationship,
      );

      if (!emergencyContactRelationship) {
        newRelationshipOptionList = [
          ...newRelationshipOptionList,
          {
            id: Date.now(),
            label: info.secondary_emergency_contact_relationship,
            value: info.secondary_emergency_contact_relationship,
          },
        ];

        setRelationshipOptionList([...newRelationshipOptionList]);
      }
    }

    setForm({
      secondary_emergency_contact_email: info.secondary_emergency_contact_email,
      secondary_emergency_contact_last_name: info.secondary_emergency_contact_last_name,
      secondary_emergency_contact_first_name: info.secondary_emergency_contact_first_name,
      secondary_emergency_contact_phone_number: info.secondary_emergency_contact_country_code
        ? `${info.secondary_emergency_contact_country_code}${info.secondary_emergency_contact_phone_number}`
        : info.secondary_emergency_contact_phone_number,
      secondary_emergency_contact_address_city: info.secondary_emergency_contact_address_city,
      secondary_emergency_contact_address_state: info.secondary_emergency_contact_address_state,
      secondary_emergency_contact_address_line_1: info.secondary_emergency_contact_address_line_1,
      secondary_emergency_contact_address_line_2: info.secondary_emergency_contact_address_line_2,
      secondary_emergency_contact_address_zip_code: info.secondary_emergency_contact_address_zip_code,
      secondary_emergency_contact_relationship:
        relationshipOptionList.find(option => option.value === info.secondary_emergency_contact_relationship) || '',
      secondary_emergency_contact_address_country:
        countryOptionList.find(country => country.value === info.secondary_emergency_contact_address_country) || '',
    });
  }, [info]);

  return (
    <ProfileFormGrid>
      <ProfileTextInput
        id="secondary_emergency_contact_first_name"
        label="First Name"
        placeholder="Enter first name"
        value={form.secondary_emergency_contact_first_name}
        synced={readonly.includes('secondary_emergency_contact_first_name')}
        handleOnChange={event => {
          setForm({ ...form, secondary_emergency_contact_first_name: event.target.value });
        }}
      />
      <ProfileTextInput
        id="secondary_emergency_contact_last_name"
        label="Last Name"
        placeholder="Enter last name"
        value={form.secondary_emergency_contact_last_name}
        synced={readonly.includes('secondary_emergency_contact_last_name')}
        handleOnChange={event => {
          setForm({ ...form, secondary_emergency_contact_last_name: event.target.value });
        }}
      />
      <ProfileSelectInput
        id="secondary_emergency_contact_relationship"
        label="Relationship"
        placeholder="Select relationship"
        value={form.secondary_emergency_contact_relationship}
        options={relationshipOptionList}
        synced={readonly.includes('secondary_emergency_contact_relationship')}
        handleOnChange={event => {
          setForm({ ...form, secondary_emergency_contact_relationship: event });
        }}
      />
      <ProfileTextInput
        id="secondary_emergency_contact_email"
        type="email"
        label="Email"
        placeholder="Enter email"
        value={form.secondary_emergency_contact_email}
        synced={readonly.includes('secondary_emergency_contact_email')}
        handleOnChange={event => {
          setForm({ ...form, secondary_emergency_contact_email: event.target.value });
        }}
      />

      {readonly.includes('secondary_emergency_contact_phone_number') ? (
        <ProfileTextInput
          id="secondary_emergency_contact_phone_number"
          label="Phone"
          value={form.secondary_emergency_contact_phone_number}
          synced={true}
        />
      ) : (
        <ProfilePhoneNumberInput
          id="secondary_emergency_contact_phone_number"
          label="Phone"
          value={form.secondary_emergency_contact_phone_number}
          handleOnChange={event => {
            setForm({ ...form, secondary_emergency_contact_phone_number: event });
          }}
        />
      )}

      <GridColumnPlaceholder />
      <ProfileTextInput
        id="secondary_emergency_contact_address_line_1"
        className="col-span-2"
        label="Address"
        labelHelperText="(Street Address or P.O. Box)"
        placeholder="Enter address"
        value={form.secondary_emergency_contact_address_line_1}
        synced={readonly.includes('secondary_emergency_contact_address_line_1')}
        handleOnChange={event => {
          setForm({ ...form, secondary_emergency_contact_address_line_1: event.target.value });
        }}
      />
      <ProfileTextInput
        id="secondary_emergency_contact_address_line_2"
        className="col-span-2"
        label="Address Line 2"
        placeholder="Enter address line 2"
        value={form.secondary_emergency_contact_address_line_2}
        synced={readonly.includes('secondary_emergency_contact_address_line_2')}
        handleOnChange={event => {
          setForm({ ...form, secondary_emergency_contact_address_line_2: event.target.value });
        }}
      />
      <GridColumnPlaceholder />
      <ProfileTextInput
        id="secondary_emergency_contact_address_city"
        label="City"
        placeholder="Enter city"
        value={form.secondary_emergency_contact_address_city}
        synced={readonly.includes('secondary_emergency_contact_address_city')}
        handleOnChange={event => {
          setForm({ ...form, secondary_emergency_contact_address_city: event.target.value });
        }}
      />
      <ProfileTextInput
        id="secondary_emergency_contact_address_state"
        label="State / Province / Territory"
        placeholder="Enter state / province / territory"
        value={form.secondary_emergency_contact_address_state}
        synced={readonly.includes('secondary_emergency_contact_address_state')}
        handleOnChange={event => {
          setForm({ ...form, secondary_emergency_contact_address_state: event.target.value });
        }}
      />
      <GridColumnPlaceholder />
      <ProfileTextInput
        id="secondary_emergency_contact_address_zip_code"
        label="Zip / Postal Code"
        placeholder="Enter zip / postal code"
        value={form.secondary_emergency_contact_address_zip_code}
        synced={readonly.includes('secondary_emergency_contact_address_zip_code')}
        handleOnChange={event => {
          setForm({ ...form, secondary_emergency_contact_address_zip_code: event.target.value });
        }}
      />
      <ProfileSelectInput
        id="secondary_emergency_contact_address_country"
        label="Country"
        placeholder="Select country"
        value={form.secondary_emergency_contact_address_country}
        options={countryOptionList}
        synced={readonly.includes('secondary_emergency_contact_address_country')}
        handleOnChange={event => {
          setForm({ ...form, secondary_emergency_contact_address_country: event });
        }}
      />
    </ProfileFormGrid>
  );
}

SecondaryEmergencyContactForm.propTypes = {
  form: PropTypes.shape({
    secondary_emergency_contact_email: PropTypes.string,
    secondary_emergency_contact_last_name: PropTypes.string,
    secondary_emergency_contact_first_name: PropTypes.string,
    secondary_emergency_contact_phone_number: PropTypes.string,
    secondary_emergency_contact_address_city: PropTypes.string,
    secondary_emergency_contact_address_state: PropTypes.string,
    secondary_emergency_contact_address_line_1: PropTypes.string,
    secondary_emergency_contact_address_line_2: PropTypes.string,
    secondary_emergency_contact_address_zip_code: PropTypes.string,
    secondary_emergency_contact_relationship: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
    secondary_emergency_contact_address_country: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  }).isRequired,
  setForm: PropTypes.func.isRequired,
};
